import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ROUTE_PATHS } from 'core/constants/routePath'
import { useDebugRequetesQuery } from 'iadocs/api/debug'
import { DebugMultiSelect } from 'iadocs/components/DebugMultiSelect'
import { DebugPagination } from 'iadocs/components/DebugPagination'
import {
  DOCUMENT_PER_PAGE_OPTIONS,
  STATUT_TECH_OPTIONS,
  TYPE_DOCUMENT_OPTIONS,
} from 'iadocs/const/debugOptions'
import { getDebugErrorMessage } from 'iadocs/debug/display'
import {
  readListParam,
  readPositiveInt,
  readTextParam,
  useDebugHeader,
} from 'iadocs/debug/search'
import { DebugRequeteFilters } from 'iadocs/interfaces/IDebug'

const DEFAULT_PER_PAGE = 100

export const DebugRequetesPage = () => {
  useDebugHeader()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const page = readPositiveInt(searchParams.get('page'), 1)
  const perPage = readPositiveInt(
    searchParams.get('perPage'),
    DEFAULT_PER_PAGE,
    100
  )
  const filters = useMemo<DebugRequeteFilters>(
    () => ({
      statutTech: readTextParam(searchParams, 'statutTech'),
      typeDocuments: readListParam(searchParams, 'typeDocuments'),
      partenaire: readTextParam(searchParams, 'partenaire'),
      origine: readTextParam(searchParams, 'origine'),
      idDossier: readTextParam(searchParams, 'idDossier'),
      idDossierPartenaire: readTextParam(searchParams, 'idDossierPartenaire'),
    }),
    [searchParams]
  )
  const [draft, setDraft] = useState(filters)

  useEffect(() => {
    setDraft(filters)
  }, [filters])

  const { data, isLoading, isFetching, isError, error } = useDebugRequetesQuery(
    { page, perPage, filters }
  )
  const items = data?.items ?? []

  const writeParams = (
    nextFilters: DebugRequeteFilters,
    nextPage: number,
    nextPerPage: number
  ) => {
    const params = new URLSearchParams()
    params.set('page', String(nextPage))
    params.set('perPage', String(nextPerPage))
    if (nextFilters.statutTech) {
      params.set('statutTech', nextFilters.statutTech)
    }
    nextFilters.typeDocuments.forEach((value) =>
      params.append('typeDocuments[]', value)
    )
    if (nextFilters.partenaire) {
      params.set('partenaire', nextFilters.partenaire)
    }
    if (nextFilters.origine) {
      params.set('origine', nextFilters.origine)
    }
    if (nextFilters.idDossier) {
      params.set('idDossier', nextFilters.idDossier)
    }
    if (nextFilters.idDossierPartenaire) {
      params.set('idDossierPartenaire', nextFilters.idDossierPartenaire)
    }
    setSearchParams(params)
  }

  const onFilter = (event: FormEvent) => {
    event.preventDefault()
    writeParams(
      {
        statutTech: draft.statutTech,
        typeDocuments: draft.typeDocuments,
        partenaire: draft.partenaire?.trim() || null,
        origine: draft.origine?.trim() || null,
        idDossier: draft.idDossier?.trim() || null,
        idDossierPartenaire: draft.idDossierPartenaire?.trim() || null,
      },
      1,
      perPage
    )
  }

  const openDetail = (id: number) => {
    navigate({
      pathname: `${ROUTE_PATHS.IADOCS_DEBUG}/requetes/${id}`,
      search: searchParams.toString(),
    })
  }

  return (
    <div className="iadocs-debug">
      <div className="debug-card">
        <div className="debug-header">
          <h1>Debug - Requêtes IADocs</h1>
        </div>

        <form className="debug-filters" onSubmit={onFilter}>
          <div className="debug-filters-requetes">
            <div className="debug-field">
              <label className="debug-label" htmlFor="statutTech">
                Statut tech
              </label>
              <select
                className="debug-input"
                id="statutTech"
                value={draft.statutTech ?? ''}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    statutTech: event.target.value || null,
                  }))
                }
              >
                <option value="">Tous</option>
                {STATUT_TECH_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <DebugMultiSelect
              id="typeDocumentsDropdown"
              label="Type document"
              options={TYPE_DOCUMENT_OPTIONS}
              selected={draft.typeDocuments}
              onChange={(typeDocuments) =>
                setDraft((current) => ({ ...current, typeDocuments }))
              }
            />
            <div className="debug-filters-actions">
              <button className="debug-btn debug-btn-primary" type="submit">
                Filtrer
              </button>
              <button
                className="debug-btn"
                type="button"
                onClick={() =>
                  setSearchParams(
                    new URLSearchParams({ perPage: String(perPage) })
                  )
                }
              >
                Réinitialiser
              </button>
            </div>
          </div>
          <div className="debug-filters-text">
            {(
              [
                ['partenaire', 'Partenaire'],
                ['origine', 'Origine'],
                ['idDossier', 'ID dossier'],
                ['idDossierPartenaire', 'ID dossier partenaire'],
              ] as const
            ).map(([key, label]) => (
              <div className="debug-field" key={key}>
                <label className="debug-label" htmlFor={key}>
                  {label}
                </label>
                <input
                  className="debug-input"
                  id={key}
                  placeholder="Contient..."
                  value={draft[key] ?? ''}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      [key]: event.target.value,
                    }))
                  }
                />
              </div>
            ))}
          </div>
        </form>

        {isError && (
          <div className="debug-flash error">{getDebugErrorMessage(error)}</div>
        )}

        {isLoading ? (
          <div className="debug-empty">Chargement...</div>
        ) : items.length > 0 ? (
          <div className="debug-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Date création</th>
                  <th>Partenaire</th>
                  <th>Type document</th>
                  <th>Statut tech</th>
                  <th>Origine</th>
                  <th>ID dossier</th>
                  <th>ID dossier partenaire</th>
                  <th>Client ID</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className={
                      item.statutTech !== 'ABOUTIE'
                        ? 'debug-row debug-row-ko'
                        : 'debug-row'
                    }
                    tabIndex={0}
                    role="link"
                    aria-label={`Voir le détail de la requête ${item.id}`}
                    onClick={() => openDetail(item.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        openDetail(item.id)
                      }
                    }}
                  >
                    <td>{item.id}</td>
                    <td>{item.dateCreation ?? ''}</td>
                    <td>{item.partenaire}</td>
                    <td>{item.typeDocument}</td>
                    <td>{item.statutTech}</td>
                    <td>{item.origine}</td>
                    <td>{item.idDossier}</td>
                    <td>{item.idDossierPartenaire}</td>
                    <td>{item.clientId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : !isError ? (
          <div className="debug-empty">Aucune requête trouvée.</div>
        ) : null}

        <DebugPagination
          page={data?.page ?? page}
          totalPages={data?.totalPages ?? 1}
          total={data?.total ?? 0}
          perPage={perPage}
          perPageOptions={DOCUMENT_PER_PAGE_OPTIONS}
          isFetching={isFetching && !isLoading}
          onPageChange={(nextPage) => writeParams(filters, nextPage, perPage)}
          onPerPageChange={(nextPerPage) =>
            writeParams(filters, 1, nextPerPage)
          }
        />
      </div>
    </div>
  )
}
